import { useCallback, useEffect, useState, type CSSProperties } from "react";
import clsx from "clsx";

import { getInitialsFromName, hashNameToAvatarIndex } from "./avatar-utils.ts";

import styles from "./avatar.module.css";

export interface AvatarProps {
  name: string;
  src?: string;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  onClick?: () => void;
  title?: string;
}

function sizeClass(size: NonNullable<AvatarProps["size"]>): string {
  if (size === "sm") return styles.sm;
  if (size === "md") return styles.md;
  if (size === "lg") return styles.lg;
  if (size === "xl") return styles.xl;
  return styles.md;
}

export function Avatar({
  name,
  src,
  size = "md",
  className,
  onClick,
  title,
}: AvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [src]);

  const showImage = Boolean(src) && !imgFailed;

  const onImgError = useCallback(() => {
    setImgFailed(true);
  }, []);

  const rootClasses = clsx(
    styles.root,
    sizeClass(size),
    className,
    onClick && styles.clickable
  );

  if (showImage) {
    return (
      <span className={rootClasses} onClick={onClick} title={title}>
        <img src={src} alt={name} className={styles.img} onError={onImgError} />
      </span>
    );
  }

  const initials = getInitialsFromName(name);
  const surfaceIndex = hashNameToAvatarIndex(name);
  const initialsSurfaceStyle = {
    backgroundColor: `var(--avatar-surface-${surfaceIndex})`,
  } satisfies CSSProperties;

  return (
    <span
      role="img"
      aria-label={name}
      className={rootClasses}
      style={initialsSurfaceStyle}
      onClick={onClick}
      title={title}
    >
      {initials}
    </span>
  );
}
